// The Photohawk Personal waitlist: the home page's Personal hero form posts here.
//
// Contract (photohawk-serverless src/stacks/personal, lib/http.js + lib/core.js parseWaitlist):
//   POST https://personal.api.<domain>/personal/waitlist
//   { email, consent: true, consentVersion?, source?, locale? }  → 200 { ok: true }
//   400 { message, field: "email" | "consent" } · 429 (Retry-After) · CORS: any origin.
// The same 200 whether or not the address is already on the list.
//
// The Personal API runs on staging only until the owner turns prod on, so prod has no base URL and the form
// reports "not open yet" instead of posting anywhere.

import { getPhotohawkEnv, type PhotohawkEnv } from "@/lib/env";

/** The Personal API per environment; null where it isn't deployed. */
export const PERSONAL_API_BASE: Record<PhotohawkEnv, string | null> = {
  staging: "https://personal.api.photohawk-staging.com",
  prod: null,
};

export const personalApiBase = (env: PhotohawkEnv = getPhotohawkEnv()): string | null => PERSONAL_API_BASE[env];

/** Stored with the signup, so a later wording change can be told apart. Bump with the consent text. */
export const WAITLIST_CONSENT_VERSION = "web-waitlist-v1";

export type WaitlistResult =
  | { ok: true }
  | { ok: false; reason: "invalid-email" | "rate-limited" | "unavailable" | "failed" };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const looksLikeEmail = (s: string): boolean => EMAIL_RE.test(s.trim());

export async function joinWaitlist(
  email: string,
  opts: { locale?: string; source?: string; base?: string | null; fetchImpl?: typeof fetch } = {},
): Promise<WaitlistResult> {
  const value = email.trim();
  if (!looksLikeEmail(value)) return { ok: false, reason: "invalid-email" };
  const base = opts.base === undefined ? personalApiBase() : opts.base;
  if (!base) return { ok: false, reason: "unavailable" };
  const doFetch = opts.fetchImpl ?? fetch;
  try {
    const res = await doFetch(`${base}/personal/waitlist`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: value,
        consent: true,
        consentVersion: WAITLIST_CONSENT_VERSION,
        source: opts.source ?? "web-home",
        locale: opts.locale,
      }),
    });
    if (res.ok) return { ok: true };
    if (res.status === 429) return { ok: false, reason: "rate-limited" };
    if (res.status === 400) {
      const body = (await res.json().catch(() => ({}))) as { field?: string };
      if (body.field === "email") return { ok: false, reason: "invalid-email" };
    }
    return { ok: false, reason: "failed" };
  } catch {
    return { ok: false, reason: "failed" };
  }
}
